// Loading Stripe.js, with one retry. Kept free of imports so it runs under
// `node --test` (stripe-load.test.mjs); lib/stripe.js wires it up.
//
// Why our own loader: @stripe/stripe-js caches its first script promise, so
// once "Failed to load Stripe.js" (a blip on a phone, a content blocker,
// $exception on 2 Oct 2026) it can never be retried in that page. Here a
// failed <script> is removed and a fresh one tried once more after a short
// wait; only then do we give up, and give up quietly (null, never a
// rejection), so the page can offer Stripe's hosted card page instead.

export const STRIPE_JS_URL = 'https://js.stripe.com/v3'
export const RETRY_DELAY_MS = 1500

// Resolves once window.Stripe exists; rejects if the script fails.
export const injectStripeJs = (doc = globalThis.document, win = globalThis.window) => new Promise((resolve, reject) => {
  if (win && win.Stripe) { resolve(); return }
  try {
    const script = doc.createElement('script')
    script.src = STRIPE_JS_URL
    script.async = true
    script.addEventListener('load', () => (win.Stripe ? resolve() : reject(new Error('Stripe.js not available'))))
    script.addEventListener('error', () => {
      try { script.remove() } catch (e) { /* already gone */ }
      reject(new Error('Failed to load Stripe.js'))
    })
    ;(doc.head || doc.body).appendChild(script)
  } catch (err) {
    reject(err)
  }
})

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms))

// true when `load` succeeded on the first or the second try. Each failed try
// is reported: onAttemptFailed(error, attempt, willRetry).
export const loadWithRetry = async ({ load, wait = sleep, retryDelayMs = RETRY_DELAY_MS, onAttemptFailed = () => {} }) => {
  try {
    await load()
    return true
  } catch (err) {
    onAttemptFailed(err, 1, true)
  }
  await wait(retryDelayMs)
  try {
    await load()
    return true
  } catch (err) {
    onAttemptFailed(err, 2, false)
    return false
  }
}

// What may leave the browser about a Stripe error: its code, decline code,
// type and message. No PII: Stripe's messages are generic, but an email or a
// long digit run (a card or phone number echoed back) is masked anyway.
export const stripeErrorDetails = error => {
  if (!error || typeof error !== 'object') return {}
  const message = typeof error.message === 'string'
    ? error.message.replace(/[^\s@]+@[^\s@]+/g, '[email]').replace(/\d[\d\s-]{6,}\d/g, '[number]').slice(0, 200)
    : undefined
  const details = {
    error_code: error.code,
    decline_code: error.decline_code,
    error_type: error.type,
    // The parameter Stripe blamed (docs.stripe.com/api/errors `param`),
    // e.g. payment_method_data[billing_details][email].
    error_param: typeof error.param === 'string' ? error.param.slice(0, 100) : undefined,
    error_message: message
  }
  Object.keys(details).forEach(key => { if (details[key] === undefined || details[key] === null || details[key] === '') delete details[key] })
  return details
}

// The same keys from our own API's error answer (Api::V2::OrdersController
// with_checkout_errors puts Stripe's code, decline code and type in `data`).
export const apiErrorDetails = res => {
  const data = res && typeof res.data === 'object' && res.data ? res.data : {}
  return stripeErrorDetails({ code: data.error_code, decline_code: data.decline_code, type: data.error_type, param: data.error_param, message: res?.message })
}
